import { useCallback, useEffect, useRef } from 'react'
import { api } from '@/lib/api'
import { queryClient } from '@/lib/queryClient'
import { trackFormLifecycle, type TelemetryForm } from '@/lib/analytics'
import { assistantKeys, type AssistantSuggestion } from './useAssistantSuggestion'

/**
 * EP-73 — Tier-1 form-lifecycle telemetry for the AI coach.
 *
 * Emits exactly two name-embedded PostHog events per form session:
 *   form_started:<form>    on the FIRST meaningful edit (once per mount)
 *   form_submitted:<form>  on successful submit
 *
 * `form_started:X` present with no `form_submitted:X` is the coach's
 * abandonment signal. No field VALUES are ever captured — only the two
 * lifecycle facts. Replaces the old localStorage draft idea entirely.
 *
 * On ABANDONMENT (started, never submitted, now unmounting) it also pings the
 * coach directly (`POST /api/assistant/abandoned_form`) so a "resume this
 * form" Tier-1 suggestion supersedes the day's generic nudge IMMEDIATELY —
 * bypassing the once/day cache + PostHog ingestion latency — and seeds the
 * suggestion cache so the bubble updates without a refetch. Best-effort: any
 * non-200 (incl. 403 when the coach is off) is a silent no-op.
 *
 * Usage:
 *   const { markStarted, markSubmitted } = useFormTelemetry('invoice')
 *   <input onChange={(e) => { markStarted(); field.onChange(e) }} />
 *   onSubmit: async () => { await save(); markSubmitted() }
 */
export function useFormTelemetry(form: TelemetryForm) {
  const startedRef = useRef(false)

  const markStarted = useCallback(() => {
    if (startedRef.current) return
    startedRef.current = true
    trackFormLifecycle('started', form)
  }, [form])

  const markSubmitted = useCallback(() => {
    trackFormLifecycle('submitted', form)
    // Allow a fresh started event if the same mounted form is reused
    // (e.g. "save and add another"). Also clears the abandonment flag.
    startedRef.current = false
  }, [form])

  useEffect(() => {
    return () => {
      if (!startedRef.current) return
      // Best-effort and defensive: this runs in an unmount effect, so it must
      // never throw — Promise.resolve() tolerates any api/mock return shape.
      try {
        const pending = api.post<{ data: AssistantSuggestion }>(
          '/api/assistant/abandoned_form',
          { form },
          { validateStatus: (s) => s === 200 || s === 204 || s === 403 },
        )
        void Promise.resolve(pending)
          .then((res) => {
            if (res?.status === 200 && res.data?.data) {
              // Seed the cache so the bubble shows the resume even though its
              // once/day fetch gate (captured at mount) won't refetch.
              queryClient.setQueryData(assistantKeys.suggestion(), res.data.data)
            }
          })
          .catch(() => {})
      } catch {
        /* coach is best-effort — never surface to the form */
      }
    }
    // `form` is constant per usage; run the abandonment check only on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { markStarted, markSubmitted }
}
